import { Link } from 'react-router-dom';
import { useInView } from '../../../hooks/useInView';

interface FinalCTAProps {
  title?: string;
  subtitle?: string;
}

export default function FinalCTA({
  title = "Let's Create Something That Commands Attention",
  subtitle = "Your brand's most powerful moment starts with a single conversation.",
}: FinalCTAProps) {
  const { ref, inView } = useInView();

  return (
    <section className="relative overflow-hidden min-h-[75vh] flex items-center justify-center">
      {/* Full-bleed background */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/7110a62bbdcace6b3d6837f13a22a1cc.jpg"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#020202] via-[#020202]/65 to-[#020202]/55" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#020202]/50 to-[#020202]/30" />
        <div className="absolute inset-0 bg-grain opacity-25 pointer-events-none" />
      </div>

      {/* Gold radial glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, rgba(201,168,76,0.06) 0%, transparent 70%)' }}
      />

      <div
        ref={ref as React.RefObject<HTMLDivElement>}
        className={`relative z-10 w-full max-w-5xl mx-auto px-8 lg:px-20 py-40 text-center transition-all duration-1000 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
      >
        {/* Accent ornament */}
        <div className="flex items-center justify-center gap-5 mb-12">
          <div className="w-20 h-px bg-gradient-to-r from-transparent to-gold/50" />
          <div className="flex gap-1">
            <div className="w-1.5 h-1.5 bg-gold rotate-45" />
            <div className="w-1 h-1 bg-gold/40 rotate-45 mt-0.5" />
          </div>
          <div className="w-20 h-px bg-gradient-to-l from-transparent to-gold/50" />
        </div>

        <p className="font-sans text-[0.58rem] tracking-[0.45em] uppercase text-gold mb-8 font-medium">Ready to Begin</p>

        <h2
          className="font-serif font-bold text-white leading-tight mb-8"
          style={{ fontSize: 'clamp(2.8rem, 6vw, 5.5rem)', letterSpacing: '-0.025em' }}
        >
          {title}
        </h2>
        <p className="font-sans text-gold/70 font-light mb-14 max-w-lg mx-auto leading-relaxed" style={{ fontSize: '1.1rem' }}>
          {subtitle}
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/contact"
            className="whitespace-nowrap group font-sans text-[0.68rem] font-semibold tracking-[0.22em] uppercase px-12 py-5 bg-gold text-[#020202] hover:bg-white transition-all duration-300 cursor-pointer inline-flex items-center gap-3"
          >
            Request Your Production Consultation
            <i className="ri-arrow-right-line group-hover:translate-x-1.5 transition-transform" />
          </Link>
          <a
            href="tel:+14074000000"
            rel="nofollow"
            className="whitespace-nowrap font-sans text-[0.68rem] font-medium tracking-[0.22em] uppercase px-12 py-5 border border-white/20 text-white hover:border-gold hover:text-gold transition-all duration-300 cursor-pointer inline-flex items-center gap-3"
          >
            <i className="ri-phone-line" />
            Click to Call
          </a>
        </div>

        {/* Trust signals */}
        <div className="mt-16 flex flex-wrap items-center justify-center gap-6">
          {['20+ Years Experience', '500+ Productions', 'Broadcast-Grade Quality', 'Bilingual Team', 'Travel Available'].map((t) => (
            <div key={t} className="flex items-center gap-2">
              <div className="w-1 h-1 bg-gold/50 rounded-full" />
              <span className="font-sans text-[0.6rem] tracking-[0.15em] uppercase text-white/35 whitespace-nowrap">{t}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
